import { EntityDetailsHeader, type EntityDetailsHeaderActions } from '@/components/ui/entity-details-header';
import {
	ScenarioRuleDisplayItem,
	type ScenarioRuleDisplayField
} from '@/components/ui/scenario-rule-display-item';
import { DetailSection } from '@/components/ui/details-panel';
import { Tabs, TabsContent, TabsList, TabLink } from '@/components/ui/tabs';
import { AuditTab } from '@/components/ui/audit-tab';
import type { ScenarioQuery } from '@/graphql/generated';
import { useOne } from '@/hooks/resourceQueries';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';
import { FileText, History, Target } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Define the scenario details data type based on the GraphQL query
type ScenarioDetailsData = NonNullable<ScenarioQuery['scenario']>;

interface ScenarioDetailsProps {
	scenarioId: string;
	activeTab?: string;
	onTabChange?: (tab: string) => void;
	hideTabsList?: boolean;
	showHeader?: boolean;
	onEdit?: (id: string) => void;
	onClone?: (id: string) => void;
	onDelete?: (id: string) => void;
	onOpenInNewWindow?: (id: string) => void;
	onClose?: () => void;
	onBack?: () => void;
	showBackButton?: boolean;
}

export const ScenarioEntityIcon = () => (
	<div className="flex items-center justify-center w-14 h-14 rounded-full bg-transparent border-2 border-gray-400 dark:border-gray-500 flex-shrink-0">
		<Target className="w-7 h-7 text-gray-600 dark:text-gray-300" />
	</div>
);

export const ScenarioDetailsHeader = ({
	scenario,
	className,
	...actions
}: {
	scenario: Pick<ScenarioDetailsData, 'scenarioId' | 'name'>;
	className?: string;
} & EntityDetailsHeaderActions) => {
	const { t } = useTranslation('common');
	const { t: tS } = useTranslation('scenarios');
	const displayName = scenario.name || tS('details.unnamedScenario');

	return (
		<EntityDetailsHeader
			icon={<ScenarioEntityIcon />}
			name={displayName}
			entityTypeName={t('scenario')}
			entityId={actions.entityId ?? scenario.scenarioId}
			onEdit={actions.onEdit}
			onClone={actions.onClone}
			onDelete={actions.onDelete}
			onOpenInNewWindow={actions.onOpenInNewWindow}
			onClose={actions.onClose}
			onBack={actions.onBack}
			showBackButton={actions.showBackButton}
			showCloseButton={actions.showCloseButton ?? Boolean(actions.onClose)}
			className={className}
		/>
	);
};

export function ScenarioDetails({
	scenarioId,
	activeTab,
	onTabChange,
	hideTabsList,
	showHeader,
	onEdit,
	onClone,
	onDelete,
	onOpenInNewWindow,
	onClose,
	onBack,
	showBackButton
}: ScenarioDetailsProps) {
	const { t } = useTranslation('common');
	const { t: tS } = useTranslation('scenarios');
	const { data, isLoading, isError } = useOne<ScenarioDetailsData>({
		resource: 'scenarios',
		id: scenarioId,
		meta: {
			dataPath: 'scenario'
		}
	});

	// For details pages, show loading immediately to prevent flash during navigation
	const { shouldShowLoading } = useDelayedLoading(isLoading, {
		delay: 100, // Shorter delay for details pages
		showInitialLoading: true // Show loading immediately during navigation
	});

	const scenario = data?.data;

	// Show skeleton during loading
	if (shouldShowLoading) {
		return (
			<div className="space-y-6">
				<div className="w-3/4 h-6 rounded bg-muted animate-pulse" />
				<div className="space-y-4">
					<div className="h-20 rounded bg-muted animate-pulse" />
					<div className="h-20 rounded bg-muted animate-pulse" />
				</div>
			</div>
		);
	}

	// Only show error when not loading
	if (!isLoading && (isError || !scenario)) {
		return (
			<div className="flex justify-center p-8">
				<div className="text-red-500">{tS('details.loadError')}</div>
			</div>
		);
	}

	// If we don't have data yet, don't render anything (let loading states handle it)
	if (!scenario) {
		return null;
	}

	const formatMinutes = (
		minutes?: number | null,
		t?: (key: string) => string
	) => {
		if (minutes == null) return t ? t('details.notSet') : 'Not set';
		const hours = Math.floor(minutes / 60);
		const mins = minutes % 60;
		if (hours > 0) {
			return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
		}
		return `${mins}m`;
	};

	const formatDate = (date?: string | null, t?: (key: string) => string) => {
		if (!date) return t ? t('details.notSet') : 'Not set';
		const parsed = new Date(date);
		return Number.isNaN(parsed.getTime())
			? t
				? t('details.notSet')
				: 'Not set'
			: parsed.toLocaleDateString();
	};

	return (
		<div className="flex flex-col h-full">
			{/* Header - shown when in modal mode */}
			{showHeader && (
				<ScenarioDetailsHeader
					scenario={scenario}
					entityId={scenarioId}
					onEdit={onEdit}
					onClone={onClone}
					onDelete={onDelete}
					onOpenInNewWindow={onOpenInNewWindow}
					onClose={onClose}
					onBack={onBack}
					showBackButton={showBackButton}
					showCloseButton={!!onClose}
					className="pb-4 mb-4"
				/>
			)}

		<Tabs
			defaultValue={activeTab !== undefined ? undefined : 'details'}
			value={activeTab}
			onValueChange={onTabChange}
			className="w-full"
		>
			{!hideTabsList && (
				<TabsList>
					<TabLink value="details" icon={FileText} label={t('tabs.details')} />
					<TabLink value="audit" icon={History} label={t('tabs.audit')} align="right" />
				</TabsList>
			)}

			<TabsContent
				value="details"
				className={hideTabsList ? 'space-y-6 mt-0' : 'space-y-6 mt-6'}
			>
				{scenario.scenarioConditions &&
					scenario.scenarioConditions.length > 0 && (
						<DetailSection title={t('sections.scenario_rules')} noBorderTop>
							<div className="text-sm">
								{scenario.scenarioConditions.map(
									(scenarioCondition, index) => {
										const condition =
											scenarioCondition.condition;
										if (!condition) return null;

										const vehicleTypes =
											condition.conditionVehicleTypes?.flatMap(
												item =>
													item?.vehicleType?.name
														? [item.vehicleType.name]
														: []
											) ?? [];
										const customerCategories =
											condition.conditionCustomerCategories?.flatMap(
												item =>
													item?.customerCategory
														?.description
														? [
																item
																	.customerCategory
																	.description
															]
														: []
											) ?? [];

										// Create array of fields to display, only including those with meaningful values
										const fieldsToShow: ScenarioRuleDisplayField[] =
											[];

										// Time-based conditions
										if (condition.weeklyDateSet?.name) {
											fieldsToShow.push({
												label: tS('details.weeklyDateSet'),
												value: condition.weeklyDateSet.name
											});
										}

										if (condition.appliesOnHolidays) {
											fieldsToShow.push({
												label: tS(
													'details.appliesOnHolidays'
												),
												value: `${tS('details.appliesOnHolidays')}: ${tS('details.yes')}`
											});
										}

										if (condition.startDate) {
											fieldsToShow.push({
												label: tS('details.startDate'),
												value: formatDate(
													condition.startDate,
													tS
												)
											});
										}

										if (condition.endDate) {
											fieldsToShow.push({
												label: tS('details.endDate'),
												value: formatDate(
													condition.endDate,
													tS
												)
											});
										}

										// Arrival/Departure times - only show if specific times are set
										if (
											condition.arrivingSince ||
											condition.arrivingUntil
										) {
											fieldsToShow.push({
												label: tS('details.arrivingTime'),
												value: `${condition.arrivingSince ?? tS('details.any')} - ${condition.arrivingUntil ?? tS('details.any')}`
											});
										}

										if (
											condition.leavingSince ||
											condition.leavingUntil
										) {
											fieldsToShow.push({
												label: tS('details.leavingTime'),
												value: `${condition.leavingSince ?? tS('details.any')} - ${condition.leavingUntil ?? tS('details.any')}`
											});
										}

										// Stay duration - only show if limits are set
										if (condition.minStayMinutes != null) {
											fieldsToShow.push({
												label: tS('details.minStay'),
												value: formatMinutes(
													condition.minStayMinutes,
													tS
												)
											});
										}

										if (condition.maxStayMinutes != null) {
											fieldsToShow.push({
												label: tS('details.maxStay'),
												value: formatMinutes(
													condition.maxStayMinutes,
													tS
												)
											});
										}

										// Overstay options - only show if enabled
										if (condition.isOverstay) {
											fieldsToShow.push({
												label: tS('details.overstay'),
												value: `${tS('details.overstay')}: ${tS('details.enabled')}`
											});
										}

										if (condition.isContractOverstay) {
											fieldsToShow.push({
												label: tS(
													'details.contractOverstay'
												),
												value: `${tS('details.contractOverstay')}: ${tS('details.enabled')}`
											});
										}

										// Vehicle and customer restrictions - only show if specific types/categories are set
										if (vehicleTypes.length > 0) {
											fieldsToShow.push({
												label: tS('details.vehicleTypes'),
												value: vehicleTypes.join(', ')
											});
										}

										if (customerCategories.length > 0) {
											fieldsToShow.push({
												label: tS(
													'details.customerCategories'
												),
												value: customerCategories.join(', ')
											});
										}

										// If no specific conditions are set, show a message
										if (fieldsToShow.length === 0) {
											fieldsToShow.push({
												label: tS('details.conditions'),
												value: tS('details.noConditions')
											});
										}

										return (
											<div
												key={condition.conditionId}
												className="py-2 border-b-2 border-dotted border-border/60 last:border-b-0 first:pt-0 last:pb-0"
											>
												<ScenarioRuleDisplayItem
													ruleLabel={`#${index + 1}`}
													fields={fieldsToShow}
												/>
											</div>
										);
									}
								)}
							</div>
						</DetailSection>
					)}
			</TabsContent>

			<TabsContent value="audit" className="mt-6">
				<AuditTab
					data={{}}
				/>
			</TabsContent>
		</Tabs>
		</div>
	);
}
